import { isNativeApp } from '@/lib/platform';

/**
 * Ouvre un document (URL signée à courte durée) dans un nouvel onglet.
 *
 * - **Web** : on ouvre l'onglet SYNCHRONEMENT (dans le geste de l'utilisateur)
 *   puis on y charge l'URL une fois signée — un `window.open` posé APRÈS l'`await`
 *   est bloqué par Safari. `opener` est coupé (l'onglet n'a aucun accès à l'app).
 *   Si le pop-up est refusé malgré tout, on retombe sur une navigation directe.
 * - **Natif** : navigateur intégré (`@capacitor/browser`, bouton « Terminé »),
 *   jamais une navigation qui remplacerait l'app dans la WebView.
 *
 * `resolveUrl` peut échouer (fichier supprimé, droits perdus) : l'onglet vide est
 * alors refermé et l'erreur remonte à l'appelant.
 */
export async function openDocumentUrl(resolveUrl: () => Promise<string>): Promise<void> {
  if (isNativeApp()) {
    const url = await resolveUrl();
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
    return;
  }

  const win = window.open('', '_blank');
  try {
    const url = await resolveUrl();
    if (win) {
      win.opener = null;
      win.location.href = url;
    } else {
      window.location.assign(url);
    }
  } catch (err) {
    win?.close();
    throw err;
  }
}
